import { useState, useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { addAmbassador } from "@/modules/ambassadors/api";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import useAuth from "@/modules/common/hooks/auth.hook";
import CountryPhoneInput from "@/modules/common/components/input/country-telephone";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";

interface AddAmbassadorModalProps {
  open: boolean;
  onClose: () => void;
}

export function AddAmbassadorModal({ open, onClose }: AddAmbassadorModalProps) {
  const theme = useTheme();
  const { user } = useAuth();
  const { isMobile } = useScreenSize();

  const [phoneNumber, setPhoneNumber] = useState("");

  const formatPhoneNumber = (phone: string) => phone.trim().replace(/\s/g, "");

  const { mutate: addAmbassadorMutation, isPending } = useMutation({
    mutationFn: addAmbassador,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getAmbassadors"] });
      setPhoneNumber("");
      onClose();
      showToast({
        type: ToastType.SUCCESS,
        message: "Ambassador added successfully!",
        toastId: TOAST_IDS.ADD_AMBASSADOR_SUCCESS,
      });
    },
    onError: (error: unknown) => {
      console.error("Failed to add ambassador:", error);
      showToast({
        type: ToastType.ERROR,
        message: "Failed to add ambassador. Please try again.",
        toastId: TOAST_IDS.ADD_AMBASSADOR_ERROR,
      });
    },
  });

  // --- Handlers ---
  const handlePhoneChange = useCallback((phone: string) => {
    setPhoneNumber(phone);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!phoneNumber.trim()) return;

    addAmbassadorMutation({
      phone: formatPhoneNumber(phoneNumber),
      invitationTypeId: 1,
      salesAgentDefaultEmail: user?.email ?? "",
    });
  }, [phoneNumber, user?.email, addAmbassadorMutation]);

  const handleClose = useCallback(() => {
    setPhoneNumber("");
    onClose();
  }, [onClose]);

  return (
    <ResponsiveEditDialog
      open={open}
      handleCloseModal={handleClose}
      header="Invite Ambassador"
      handleConfirm={handleSubmit}
      isLoading={isPending}
      disabledConfirmButton={!phoneNumber.trim()}
      confirmText="Add Ambassador"
      cancelText="Cancel"
      setOpen={() => {}}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pt: theme.nawy.spacing.xs,
          gap: theme.nawy.spacing.md,
          minWidth: isMobile ? "100%" : 600,
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: nawyTheme.nawy.colors.primitives.grey[700],
              mb: 1,
            }}
          >
            Phone number
          </Typography>
          <CountryPhoneInput
            value={phoneNumber}
            onPhoneChange={handlePhoneChange}
            size="medium"
          />
        </Box>
      </Box>
    </ResponsiveEditDialog>
  );
}
